import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { USERS } from '../../data/mockData';
import { PageHeader, Button, Card, Modal, FormField, Input, Select, Textarea, Avatar } from '../ui';
import { Plus, Send, Radio, Reply } from 'lucide-react';

function timeAgo(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const AVATAR_COLORS = ['var(--gold-main)','#43a047','#f57c00','#8e24aa','#e53935','#00897b'];

export default function Messages() {
  const { messages, sendMessage, markRead, teams } = useApp();
  const { user, isAdmin } = useAuth();
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [newForm, setNewForm] = useState({ toUserId: '', subject: '', body: '' });
  const [broadForm, setBroadForm] = useState({ subject: '', body: '', target: 'all' });

  const getUser = id => USERS.find(u => u.id === id) || { name: 'Unknown', avatar: '??' };

  // Thread grouping
  const threads = messages.reduce((acc, m) => {
    const tid = m.threadId;
    if (!acc[tid]) acc[tid] = [];
    acc[tid].push(m);
    return acc;
  }, {});

  // Summary: latest message per thread
  const threadList = Object.entries(threads).map(([tid, msgs]) => {
    const latest = msgs.sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
    const unread = msgs.some(m => !m.read && m.toUserId === user?.id);
    return { tid, latest, unread, count: msgs.length };
  }).sort((a,b) => new Date(b.latest.createdAt) - new Date(a.latest.createdAt));

  const selectedThread = selected ? threads[selected] : null;

  const handleSelect = (tid) => {
    setSelected(tid);
    threads[tid]?.forEach(m => { if (!m.read) markRead(m.id); });
  };

  const submitReply = () => {
    if (!replyText.trim() || !selected) return;
    const last = threads[selected]?.[0];
    sendMessage({ fromUserId: user.id, toUserId: last?.fromUserId === user.id ? last?.toUserId : last?.fromUserId, subject: 'Re: ' + last?.subject, body: replyText.trim(), read: false, threadId: selected });
    setReplyText('');
  };

  const submitNew = (e) => {
    e.preventDefault();
    const tid = 'th' + Date.now();
    sendMessage({ ...newForm, fromUserId: user.id, read: false, threadId: tid });
    setShowNew(false);
    setNewForm({ toUserId: '', subject: '', body: '' });
  };

  const submitBroadcast = (e) => {
    e.preventDefault();
    const tid = 'th' + Date.now();
    sendMessage({ ...broadForm, fromUserId: user.id, toUserId: null, read: false, threadId: tid, broadcast: true });
    setShowBroadcast(false);
    setBroadForm({ subject: '', body: '', target: 'all' });
  };

  const unreadTotal = messages.filter(m => !m.read && m.toUserId === user?.id).length;

  return (
    <div>
      <PageHeader title="Messages"
        subtitle={unreadTotal > 0 ? `${unreadTotal} unread message${unreadTotal !== 1 ? 's' : ''}` : 'All caught up'}
        actions={<>
          {isAdmin && <Button variant="secondary" onClick={() => setShowBroadcast(true)}><Radio size={14} /> Broadcast</Button>}
          <Button variant="primary" onClick={() => setShowNew(true)}><Plus size={15} /> New message</Button>
        </>}
      />

      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '1rem', minHeight: 500 }}>
        {/* Thread list */}
        <div style={{ background: 'var(--surface-0)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)', background: 'var(--surface-1)' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)' }}>Inbox</span>
          </div>
          {threadList.length === 0 ? (
            <p style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>No messages yet</p>
          ) : threadList.map(({ tid, latest, unread, count }) => {
            const from = getUser(latest.fromUserId);
            const colorIdx = USERS.findIndex(u => u.id === latest.fromUserId) % AVATAR_COLORS.length;
            return (
              <div key={tid} onClick={() => handleSelect(tid)}
                style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)', cursor: 'pointer', background: selected === tid ? 'var(--gold-pale)' : unread ? 'rgba(30,136,229,0.03)' : '', borderLeft: unread ? '3px solid var(--gold-main)' : '3px solid transparent', transition: 'background 0.1s' }}
                onMouseEnter={e => { if (selected !== tid) e.currentTarget.style.background = 'var(--surface-1)'; }}
                onMouseLeave={e => { if (selected !== tid) e.currentTarget.style.background = unread ? 'rgba(30,136,229,0.03)' : ''; }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ width: 34, height: 34, borderRadius: '50%', background: AVATAR_COLORS[colorIdx], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                    {from.avatar}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 4 }}>
                      <span style={{ fontWeight: unread ? 700 : 600, fontSize: 13, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{from.name}</span>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 }}>{timeAgo(latest.createdAt)}</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: unread ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{latest.subject}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1 }}>{latest.body}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Thread view */}
        {selected && selectedThread ? (
          <div style={{ background: 'var(--surface-0)', border: '1px solid var(--border)', borderRadius: 14, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', background: 'var(--surface-1)' }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{selectedThread[0]?.subject}</h3>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{selectedThread.length} message{selectedThread.length !== 1 ? 's' : ''}</p>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '1rem', display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[...selectedThread].sort((a,b) => new Date(a.createdAt) - new Date(b.createdAt)).map(msg => {
                const from = getUser(msg.fromUserId);
                const isMine = msg.fromUserId === user?.id;
                const colorIdx = USERS.findIndex(u => u.id === msg.fromUserId) % AVATAR_COLORS.length;
                return (
                  <div key={msg.id} style={{ display: 'flex', gap: 10, flexDirection: isMine ? 'row-reverse' : 'row' }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: isMine ? 'var(--brand-dark)' : AVATAR_COLORS[colorIdx], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0, marginTop: 2 }}>{from.avatar}</div>
                    <div style={{ maxWidth: '70%' }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexDirection: isMine ? 'row-reverse' : 'row', marginBottom: 4 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>{from.name}</span>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{timeAgo(msg.createdAt)}</span>
                        {msg.broadcast && <span style={{ fontSize: 10, fontWeight: 700, background: 'rgba(30,136,229,0.18)', color: '#80b0f8', padding: '1px 6px', borderRadius: 10 }}>BROADCAST</span>}
                      </div>
                      <div style={{ background: isMine ? 'var(--brand-dark)' : 'var(--surface-1)', color: isMine ? '#fff' : 'var(--text-primary)', padding: '10px 14px', borderRadius: isMine ? '14px 14px 4px 14px' : '14px 14px 14px 4px', fontSize: 14, lineHeight: 1.6 }}>
                        {msg.body}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ padding: '12px 14px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8 }}>
              <input value={replyText} onChange={e => setReplyText(e.target.value)} onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), submitReply())}
                placeholder="Write a reply… (Enter to send)"
                style={{ flex: 1, padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 10, fontSize: 14, fontFamily: 'var(--font-sans)', color: 'var(--text-primary)', background: 'var(--surface-0)', outline: 'none' }} />
              <Button variant="primary" onClick={submitReply} disabled={!replyText.trim()}><Send size={14} /></Button>
            </div>
          </div>
        ) : (
          <div style={{ background: 'var(--surface-0)', border: '1px solid var(--border)', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
            Select a conversation to read
          </div>
        )}
      </div>

      {/* New Message Modal */}
      <Modal open={showNew} onClose={() => setShowNew(false)} title="New message">
        <form onSubmit={submitNew} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <FormField label="To" required>
            <Select value={newForm.toUserId} onChange={e => setNewForm(p => ({...p, toUserId: e.target.value}))}>
              <option value="">Select recipient…</option>
              {USERS.filter(u => u.id !== user?.id).map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
            </Select>
          </FormField>
          <FormField label="Subject" required>
            <Input value={newForm.subject} onChange={e => setNewForm(p => ({...p, subject: e.target.value}))} placeholder="Message subject" required />
          </FormField>
          <FormField label="Message" required>
            <textarea value={newForm.body} onChange={e => setNewForm(p => ({...p, body: e.target.value}))} rows={5} required
              placeholder="Write your message…"
              style={{ width: '100%', padding: '8px 11px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 14, fontFamily: 'var(--font-sans)', resize: 'vertical' }} />
          </FormField>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button type="button" variant="secondary" onClick={() => setShowNew(false)}>Cancel</Button>
            <Button type="submit" variant="primary"><Send size={13} /> Send</Button>
          </div>
        </form>
      </Modal>

      {/* Broadcast Modal */}
      <Modal open={showBroadcast} onClose={() => setShowBroadcast(false)} title="Broadcast to all teams">
        <form onSubmit={submitBroadcast} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ background: 'rgba(199,163,93,0.10)', border: '1px solid rgba(199,163,93,0.28)', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: 'var(--text-secondary)' }}>
            📢 Broadcast messages are sent to all selected recipients at once.
          </div>
          <FormField label="Recipients">
            <Select value={broadForm.target} onChange={e => setBroadForm(p => ({...p, target: e.target.value}))}>
              <option value="all">All teams and coaches</option>
              <option value="coaches">Coaches only</option>
              <option value="parents">Parents only</option>
              <option value="registered">Registered teams only</option>
            </Select>
          </FormField>
          <FormField label="Subject" required>
            <Input value={broadForm.subject} onChange={e => setBroadForm(p => ({...p, subject: e.target.value}))} placeholder="e.g. Registration deadline reminder" required />
          </FormField>
          <FormField label="Message" required>
            <textarea value={broadForm.body} onChange={e => setBroadForm(p => ({...p, body: e.target.value}))} rows={6} required
              placeholder="Write your broadcast message…"
              style={{ width: '100%', padding: '8px 11px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 14, fontFamily: 'var(--font-sans)', resize: 'vertical' }} />
          </FormField>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button type="button" variant="secondary" onClick={() => setShowBroadcast(false)}>Cancel</Button>
            <Button type="submit" variant="primary"><Radio size={13} /> Send broadcast</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
